import { useState } from "react";
import type { Telemetry } from "../lib/telemetry/queue";
import { downloadCsv, downloadJson } from "../lib/telemetry/export";

/**
 * Parent-only telemetry export. Downloads ALL recorded events (from this
 * device's IndexedDB queue) as JSON or CSV. Surfaced on the end screen and via
 * ?export=1 so a child can't trigger it mid-story.
 */
export function ExportPanel({ telemetry }: { telemetry: Telemetry }) {
  const [count, setCount] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);

  async function withEvents(fn: (events: Awaited<ReturnType<Telemetry["exportEvents"]>>) => void) {
    setBusy(true);
    try {
      const events = await telemetry.exportEvents();
      setCount(events.length);
      fn(events);
    } finally {
      setBusy(false);
    }
  }

  const stamp = new Date().toISOString().slice(0, 10);

  return (
    <div className="export-panel">
      <h2>Telemetry</h2>
      <p className="muted">
        Exports every event stored on this device. Use one device per child for
        clean per-child logs.
      </p>
      <div className="export-actions">
        <button
          type="button"
          className="btn btn--ghost"
          disabled={busy}
          onClick={() =>
            withEvents((events) => downloadJson(events, `tella-events-${stamp}`))
          }
        >
          Export JSON
        </button>
        <button
          type="button"
          className="btn btn--ghost"
          disabled={busy}
          onClick={() =>
            withEvents((events) => downloadCsv(events, `tella-events-${stamp}`))
          }
        >
          Export CSV
        </button>
        <button
          type="button"
          className="btn btn--ghost"
          disabled={busy}
          onClick={() => withEvents(() => void telemetry.flush())}
        >
          Count / flush
        </button>
      </div>
      {count !== null && <p className="muted">{count} events stored.</p>}
    </div>
  );
}
